import { useMemo } from 'react'
import { ITermState, ITermStateMember } from '../common/models/Term'
import {
  EvaluationStatus,
  EvaluationAssessmentType,
} from '../common/models/Evaluation'
import { ICompany, ICompanyMember } from '../common/models/Company'
import { getMemberTermScores } from '../utils/evaluationHelpers'

const useEvaluationStatistics = (
  evaluationStates: ITermStateMember[] | undefined
) => {
  // Use useMemo to prevent unnecessary recalculations
  return useMemo(() => {
    if (!evaluationStates || evaluationStates.length === 0) {
      return null
    }

    const stats = evaluationStates.reduce(
      (acc, state) => {
        // Count done evaluations
        if (
          state.evaluation.status === EvaluationStatus.DONE &&
          state.evaluation.assessors.length > 0
        ) {
          acc.doneEvaluations += 1
        }

        // Process assessors data
        state.evaluation.assessors.forEach((assessor) => {
          acc.totalSkillAssessments += assessor.skills ? 1 : 0
          acc.totalGoalAssessments += assessor.goals ? 1 : 0
          acc.totalCultureAssessments += assessor.culture ? 1 : 0
        })

        // Process assessment data
        state.evaluation.assessment.forEach((assessment) => {
          acc.doneSkillAssessments += assessment.skillsSubmitted ? 1 : 0
          acc.doneGoalAssessments += assessment.goalsSubmitted ? 1 : 0
          acc.doneCultureAssessments += assessment.cultureSubmitted ? 1 : 0
        })

        return acc
      },
      {
        doneEvaluations: 0,
        totalSkillAssessments: 0,
        totalGoalAssessments: 0,
        totalCultureAssessments: 0,
        doneSkillAssessments: 0,
        doneGoalAssessments: 0,
        doneCultureAssessments: 0,
      }
    )

    const totalEvaluations = evaluationStates.filter(
      (e) => e.evaluation.assessors.length > 0
    ).length

    const totalTypeEvaluations = (type: EvaluationAssessmentType) =>
      evaluationStates.reduce(
        (n, e) => (e.evaluation.assessors.some((as) => as[type]) ? n + 1 : n),
        0
      )

    return {
      ...stats,
      totalEvaluations,
      percentageSkillAssessments:
        stats.totalSkillAssessments === 0
          ? 0
          : (stats.doneSkillAssessments / stats.totalSkillAssessments) * 100,
      percentageGoalAssessments:
        stats.totalGoalAssessments === 0
          ? 0
          : (stats.doneGoalAssessments / stats.totalGoalAssessments) * 100,
      percentageCultureAssessments:
        stats.totalCultureAssessments === 0
          ? 0
          : (stats.doneCultureAssessments / stats.totalCultureAssessments) *
            100,
      averageSkillAssessments: Math.round(
        stats.totalSkillAssessments / totalTypeEvaluations('skills')
      ),
      averageGoalAssessments: Math.round(
        stats.totalGoalAssessments / totalTypeEvaluations('goals')
      ),
      averageCultureAssessments: Math.round(
        stats.totalCultureAssessments / totalTypeEvaluations('culture')
      ),
    }
  }, [evaluationStates])
}

export interface IEvaluationResultAverages {
  avgSkill?: number
  avgCulture?: number
  avgGoalAchievement?: number
  measuredSkillMembers: number
  measuredCultureMembers: number
  measuredGoalMembers: number
  totalSkillMembers: number
  totalCultureMembers: number
  totalGoalMembers: number
}

/**
 * Aggregates per-member skill, culture and goal averages over a cohort
 * (visibleMembers) for a given term's evaluation states. Used to drive the
 * KPI strip and its compare-mode deltas.
 */
const useEvaluationResultAverages = (
  evaluationStates: ITermStateMember[] | undefined,
  termState: ITermState | undefined,
  members: ICompanyMember[] | undefined,
  company: ICompany | undefined
): IEvaluationResultAverages => {
  return useMemo(() => {
    const empty: IEvaluationResultAverages = {
      avgSkill: undefined,
      avgCulture: undefined,
      avgGoalAchievement: undefined,
      measuredSkillMembers: 0,
      measuredCultureMembers: 0,
      measuredGoalMembers: 0,
      totalSkillMembers: 0,
      totalCultureMembers: 0,
      totalGoalMembers: 0,
    }

    if (!evaluationStates || !members || !company) return empty

    let skillSum = 0
    let cultureSum = 0
    let goalAchievedSum = 0
    let goalTotalSum = 0
    let skillCount = 0
    let cultureCount = 0
    let goalCount = 0
    let totalSkillMembers = 0
    let totalCultureMembers = 0
    let totalGoalMembers = 0

    for (const member of members) {
      const evalState = evaluationStates.find(
        (s) => s.evaluation.memberId === member._id
      )
      if (!evalState) continue

      const scores = getMemberTermScores(member, evalState, termState, company)

      if (scores.hasSkillAssessors) totalSkillMembers += 1
      if (scores.hasCultureAssessors) totalCultureMembers += 1
      if (scores.hasGoalAssessors) totalGoalMembers += 1

      if (scores.skillAvg !== undefined) {
        skillSum += scores.skillAvg
        skillCount += 1
      }
      if (scores.cultureAvg !== undefined) {
        cultureSum += scores.cultureAvg
        cultureCount += 1
      }
      if (scores.goalsTotal > 0) {
        goalAchievedSum += scores.goalsAchieved
        goalTotalSum += scores.goalsTotal
        goalCount += 1
      }
    }

    return {
      avgSkill: skillCount === 0 ? undefined : skillSum / skillCount,
      avgCulture: cultureCount === 0 ? undefined : cultureSum / cultureCount,
      avgGoalAchievement:
        goalTotalSum === 0 ? undefined : (goalAchievedSum / goalTotalSum) * 100,
      measuredSkillMembers: skillCount,
      measuredCultureMembers: cultureCount,
      measuredGoalMembers: goalCount,
      totalSkillMembers,
      totalCultureMembers,
      totalGoalMembers,
    }
  }, [evaluationStates, termState, members, company])
}

export { useEvaluationStatistics, useEvaluationResultAverages }
